import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/permissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, CalendarDays, Clock, Edit, Loader2, ShieldCheck, RotateCcw } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ManagerLeaveCard } from '@/components/cards/ManagerLeaveCard';
import { ManagerApprovalDialog } from '@/components/manager/ManagerApprovalDialog';
import { LeaveApprovalFilterPopover } from '@/components/leave/LeaveApprovalFilterPopover';
import { useToast } from '@/hooks/use-toast';
import { useCompanyLeaveRequests, useCompanyEmployees, useLeaveMutations } from '@/hooks/leave';
import { useLeaveApprovalFilters } from '@/hooks/leave/useLeaveApprovalFilters';

import { generateInitials, generateDisplayName } from '@/utils/avatarUtils';
import { formatLeaveDuration, isPartDayRequest } from '@/utils/leaveDurationUtils';
import type { LeaveRequestWithProfile } from '@/services/leave/types';

const PENDING_PAGE_SIZE = 50;

const ManagerLeave = () => {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const canApproveLeave = useHasPermission('approve_leave_requests');
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<LeaveRequestWithProfile | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [statusReason, setStatusReason] = useState<string>('');
  const [approvalRequest, setApprovalRequest] = useState<LeaveRequestWithProfile | null>(null);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  
  // Pending requests pagination state
  const [pendingPage, setPendingPage] = useState(0);
  const [accumulatedPending, setAccumulatedPending] = useState<LeaveRequestWithProfile[]>([]);

  // Pending requests (server-side filtered to status='pending' with limit)
  const { 
    data: pendingResult,
    isLoading: pendingLoading,
  } = useCompanyLeaveRequests({ 
    status: 'pending', 
    limit: PENDING_PAGE_SIZE,
    offset: pendingPage * PENDING_PAGE_SIZE,
  });

  // Accumulate pending results for Load More
  useEffect(() => {
    if (pendingResult?.data) {
      if (pendingPage === 0) {
        setAccumulatedPending(pendingResult.data);
      } else {
        setAccumulatedPending(prev => {
          const existingIds = new Set(prev.map(r => r.id));
          const newItems = pendingResult.data.filter(r => !existingIds.has(r.id));
          return [...prev, ...newItems];
        });
      }
    }
  }, [pendingResult, pendingPage]);

  const pendingRequests = accumulatedPending;
  const pendingTotalCount = pendingResult?.totalCount ?? 0;
  const pendingHasMore = pendingResult?.hasMore ?? false;

  // Filtered requests for "All Leave Requests" section
  const {
    filters,
    updateFilter,
    appliedFilters,
    applyFilters,
    clearFilters,
    hasPendingChanges,
    activeFilterCount,
    leaveRequests: allRequests,
    totalCount: allRequestsTotalCount,
    hasMore: allRequestsHasMore,
    isLoading: filteredLoading,
    loadMore: loadMoreAllRequests,
    availableLeaveTypes,
  } = useLeaveApprovalFilters();

  const { 
    data: employees = [], 
    isLoading: employeesLoading 
  } = useCompanyEmployees();

  const { updateLeaveRequestStatus, adminUpdateLeaveRequestStatus } = useLeaveMutations();

  const loading = pendingLoading || employeesLoading;





  const openApprovalDialog = (request: LeaveRequestWithProfile) => {
    setApprovalRequest(request);
    setIsApprovalDialogOpen(true);
  };

  const handleApprovalDialogApprove = async (requestId: string, overrideReason?: string) => {
    try {
      await updateLeaveRequestStatus.mutateAsync({
        requestId,
        status: 'approved',
        reason: overrideReason || undefined,
      });
      setIsApprovalDialogOpen(false);
      setApprovalRequest(null);
    } catch (error) {
      console.error('Error approving leave request:', error);
    }
  };

  const handleApprovalDialogReject = async (requestId: string, reason?: string) => {
    try {
      await updateLeaveRequestStatus.mutateAsync({
        requestId,
        status: 'rejected',
        reason: reason || undefined,
      });
      setIsApprovalDialogOpen(false);
      setApprovalRequest(null);
    } catch (error) {
      console.error('Error rejecting leave request:', error);
    }
  };

  const handleAdminStatusUpdate = (requestId: string, newStatus: string, reason?: string) => {
    adminUpdateLeaveRequestStatus.mutate(
      { requestId, status: newStatus, reason },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
          setEditingRequest(null);
          setNewStatus('');
          setStatusReason('');
        }
      }
    );
  };

  const openEditDialog = (request: LeaveRequestWithProfile) => {
    setEditingRequest(request);
    setNewStatus(request.status);
    setStatusReason('');
    setEditDialogOpen(true);
  };

  // Reason is required when changing a non-pending request's status
  const isReasonRequired = editingRequest != null && editingRequest.status !== 'pending' && newStatus !== editingRequest.status;
  const canSave = newStatus && (!isReasonRequired || statusReason.trim().length > 0);

  const handleSaveStatus = () => {
    if (editingRequest && canSave) {
      handleAdminStatusUpdate(editingRequest.id, newStatus, statusReason.trim() || undefined);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'rejected': return 'destructive';
      case 'pending': return 'secondary';
      default: return 'outline';
    }
  };

  if (!canApproveLeave) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Leave Approvals</h1>
        <p className="text-muted-foreground">Review and approve team leave requests</p>
      </div>

      {/* Pending Requests Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-600" />
            Pending Requests ({pendingTotalCount})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingRequests.length > 0 ? (
            <>
              {isMobile ? (
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                  <ManagerLeaveCard
                      key={request.id}
                      request={request}
                      getStatusBadgeVariant={getStatusBadgeVariant}
                      onStatusUpdate={(requestId, status) => openApprovalDialog(request)}
                      onEditRequest={openEditDialog}
                      showActions={true}
                    />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarImage 
                                src={request.profile?.avatar_url} 
                                alt={generateDisplayName(request.profile?.first_name, request.profile?.last_name)}
                              />
                              <AvatarFallback>
                                {generateInitials(request.profile?.first_name, request.profile?.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span>
                              {request.profile?.first_name || 'Unknown'} {request.profile?.last_name || 'User'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{request.leave_type}</TableCell>
                        <TableCell>{format(new Date(request.start_date), 'MMM d, yyyy')}</TableCell>
                        <TableCell>{format(new Date(request.end_date), 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {formatLeaveDuration(request)}
                            {isPartDayRequest(request) && (
                              <Badge variant="outline" className="text-xs ml-1">Part Day</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {request.reason || '-'}
                        </TableCell>
                        <TableCell>{format(new Date(request.created_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openApprovalDialog(request)}
                              className="px-3"
                            >
                              Review
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {pendingHasMore && (
                <div className="flex justify-center mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setPendingPage(prev => prev + 1)}
                    disabled={pendingLoading}
                  >
                    {pendingLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Load More ({pendingRequests.length} of {pendingTotalCount})
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <CheckCircle className="h-8 w-8" />
                <p>No pending leave requests</p>
                <p className="text-sm">All requests have been processed</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Requests Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <LeaveApprovalFilterPopover
              filters={filters}
              onUpdateFilter={updateFilter}
              onApply={applyFilters}
              onClear={clearFilters}
              employees={employees}
              availableLeaveTypes={availableLeaveTypes}
              activeFilterCount={activeFilterCount}
              hasPendingChanges={hasPendingChanges}
            />
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              All Leave Requests
              {allRequestsTotalCount > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({allRequestsTotalCount})
                </span>
              )}
              {filteredLoading && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            <div className="space-y-4">
              {allRequests.map((request) => (
              <ManagerLeaveCard
                  key={request.id}
                  request={request}
                  getStatusBadgeVariant={getStatusBadgeVariant}
                  onStatusUpdate={(requestId, status) => openApprovalDialog(request)}
                  onEditRequest={openEditDialog}
                  showActions={true}
                />
              ))}
              {allRequests.length === 0 && (
                <div className="text-center py-8">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <CalendarDays className="h-8 w-8" />
                    <p>No leave requests found</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Leave Type</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage 
                            src={request.profile?.avatar_url} 
                            alt={generateDisplayName(request.profile?.first_name, request.profile?.last_name)}
                          />
                          <AvatarFallback>
                            {generateInitials(request.profile?.first_name, request.profile?.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span>
                          {request.profile?.first_name || 'Unknown'} {request.profile?.last_name || 'User'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{request.leave_type}</TableCell>
                    <TableCell>{format(new Date(request.start_date), 'MMM d, yyyy')}</TableCell>
                    <TableCell>{format(new Date(request.end_date), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {formatLeaveDuration(request)}
                        {isPartDayRequest(request) && (
                          <Badge variant="outline" className="text-xs ml-1">Part Day</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={getStatusBadgeVariant(request.status)}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </Badge>
                        {request.payroll_status === 'verified' && (
                          <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 dark:bg-green-950 dark:border-green-800">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            Payroll Verified
                          </Badge>
                        )}
                        {request.payroll_status === 'rolled_back' && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50 dark:bg-orange-950 dark:border-orange-800">
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Rolled Back
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {request.reason || '-'}
                    </TableCell>
                    <TableCell>{format(new Date(request.created_at), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openApprovalDialog(request)}
                            className="px-3"
                          >
                            Review
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(request)}
                          className="h-8 w-8 p-0"
                          title="Edit Status"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {allRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <CalendarDays className="h-8 w-8" />
                        <p>No leave requests found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          {allRequestsHasMore && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                onClick={loadMoreAllRequests}
                disabled={filteredLoading}
              >
                {filteredLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Load More ({allRequests.length} of {allRequestsTotalCount})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Approval Dialog */}
      <ManagerApprovalDialog
        request={approvalRequest}
        open={isApprovalDialogOpen}
        onOpenChange={setIsApprovalDialogOpen}
        onApprove={handleApprovalDialogApprove}
        onReject={handleApprovalDialogReject}
      />

      {/* Admin Edit Status Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Leave Request Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingRequest && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  <strong>Employee:</strong> {editingRequest.profile?.first_name} {editingRequest.profile?.last_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Leave Type:</strong> {editingRequest.leave_type}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Dates:</strong> {format(new Date(editingRequest.start_date), 'MMM d, yyyy')} - {format(new Date(editingRequest.end_date), 'MMM d, yyyy')}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="status-select">Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isReasonRequired && (
              <div className="space-y-2">
                <Label htmlFor="status-reason">Reason <span className="text-destructive">*</span></Label>
                <Textarea
                  id="status-reason"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Explain why this status is being changed..."
                  className="min-h-[80px]"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">
                  A reason is required when overriding a non-pending decision.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveStatus} disabled={!canSave}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerLeave;
